import { useEffect, useRef } from "react";

/** Intensity falls from 1 → 0 over this many ms once the cursor stops. */
const DECAY_MS = 1600;
/** No movement for this long before decay starts. */
const IDLE_MS = 70;
const TICK_MS = 70;
const MAX_SLICE_NODES = 28;
const MAX_CHROMA = 10;

/** Same palette as WaveDivider / GlitchText. */
const GLITCH_COLORS = [
	"#ea4e2c",
	"#e23722",
	"#00e5ff",
	"#ff2bd6",
	"#39ff14",
	"#ffe600",
	"#4d7cff",
	"#7cf5ff",
	"#ff6b4a",
];

function rand(min: number, max: number) {
	return min + Math.random() * (max - min);
}

function pickColor(exclude?: string) {
	const pool = exclude
		? GLITCH_COLORS.filter((color) => color !== exclude)
		: GLITCH_COLORS;
	return pool[Math.floor(Math.random() * pool.length)];
}

function clipInset(
	x: number,
	y: number,
	w: number,
	h: number,
	boxW: number,
	boxH: number,
) {
	const top = Math.max(0, y);
	const left = Math.max(0, x);
	const bottom = Math.max(0, boxH - (y + h));
	const right = Math.max(0, boxW - (x + w));
	return `inset(${top.toFixed(1)}px ${right.toFixed(1)}px ${bottom.toFixed(1)}px ${left.toFixed(1)}px)`;
}

type Slice = {
	x: number;
	y: number;
	w: number;
	h: number;
	dx: number;
	srcDx: number;
	srcDy: number;
	color: string;
	/** 0..1 local strength used for chromatic offset scaling. */
	local: number;
};

type Burst = {
	colorA: string;
	colorB: string;
	slices: Slice[];
};

/** Strong near the cursor, still present across the rest of the image. */
function localStrength(y: number, cy: number, boxH: number, globalI: number) {
	const dist = Math.abs(y - cy) / Math.max(1, boxH);
	const proximity = Math.exp(-dist * dist * 14);
	return globalI * (0.18 + 0.82 * proximity);
}

function buildBurst(
	cx: number,
	cy: number,
	boxW: number,
	boxH: number,
	intensity: number,
): Burst {
	const i = Math.max(0, Math.min(1, intensity));
	const colorA = pickColor();
	const colorB = pickColor(colorA);
	const slices: Slice[] = [];
	const lineCount = Math.max(0, Math.round((12 + 16 * i) * Math.max(0.35, i)));

	for (let n = 0; n < lineCount; n += 1) {
		// Mix full-frame coverage with extra density around the cursor.
		const y =
			Math.random() < 0.55
				? Math.max(0, Math.min(boxH, cy + rand(-boxH * 0.22, boxH * 0.22)))
				: rand(0, boxH);

		const local = localStrength(y, cy, boxH, i);
		if (local < 0.06) continue;

		// At full intensity, each line is at least 10% of the image height.
		const minH = boxH * 0.1 * i;
		const h = rand(minH, minH * (1.2 + 1.4 * local));
		// Full-width tears; near the cursor occasionally shorter hot bands.
		const full = Math.random() < 0.72 + 0.2 * (1 - local);
		const w = full ? boxW : rand(boxW * (0.45 + 0.4 * local), boxW);
		const x = full ? 0 : Math.max(0, Math.min(boxW - w, cx - w * rand(0.2, 0.8)));
		const clampedY = Math.max(0, Math.min(boxH - h, y - h * 0.5));

		slices.push({
			x,
			y: clampedY,
			w,
			h,
			dx: rand(2, 4 + 48 * local) * (Math.random() < 0.5 ? -1 : 1),
			srcDx: rand(-36, 36) * local,
			srcDy: rand(-6, 6) * local,
			color: Math.random() < 0.5 ? colorA : colorB,
			local,
		});
	}

	// Sort so the strongest (near-cursor) lines paint last / feel denser.
	slices.sort((a, b) => a.local - b.local);
	return { colorA, colorB, slices: slices.slice(0, MAX_SLICE_NODES) };
}

type GlitchPortraitProps = {
	src: string;
	srcSet?: string;
	sizes?: string;
	alt: string;
	width: number;
	height: number;
};

export default function GlitchPortrait({
	src,
	srcSet,
	sizes,
	alt,
	width,
	height,
}: GlitchPortraitProps) {
	const rootRef = useRef<HTMLElement>(null);

	useEffect(() => {
		const root = rootRef.current;
		if (!root) return;

		const chromaA = Array.from(
			root.querySelectorAll<HTMLElement>(".home-hero-glitch-chroma-a"),
		);
		const chromaB = Array.from(
			root.querySelectorAll<HTMLElement>(".home-hero-glitch-chroma-b"),
		);
		const sliceNodes = Array.from(
			root.querySelectorAll<HTMLElement>(".home-hero-glitch-slice"),
		);
		const frameA = root.querySelector<HTMLElement>(".home-hero-glitch-frame-a");
		const frameB = root.querySelector<HTMLElement>(".home-hero-glitch-frame-b");
		if (!chromaA.length || !chromaB.length || !frameA || !frameB) return;

		const reduceMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		const finePointer = window.matchMedia("(pointer: fine)").matches;

		const size = { w: 0, h: 0 };
		const cursor = { x: 0, y: 0 };
		let intensity = 0;
		let burst: Burst | null = null;
		let hovering = false;
		let lastMove = 0;
		let lastTick = 0;
		let lastFrame = 0;
		let raf = 0;

		const imgOf = (node: HTMLElement) => node.querySelector("img");

		const clearNode = (node: HTMLElement) => {
			node.style.opacity = "0";
			node.style.clipPath = "inset(50% 50% 50% 50%)";
			node.style.transform = "none";
			node.style.removeProperty("--glitch-color");
			const img = imgOf(node);
			if (img) img.style.transform = "none";
		};

		const paintNode = (
			node: HTMLElement,
			band: Slice,
			chromaPx: number,
			side: -1 | 0 | 1,
			color?: string,
		) => {
			const img = imgOf(node);
			if (!img) return;
			const shiftX = band.dx + side * chromaPx * band.local;
			if (color) node.style.setProperty("--glitch-color", color);
			else node.style.removeProperty("--glitch-color");
			node.style.clipPath = clipInset(band.x, band.y, band.w, band.h, size.w, size.h);
			node.style.transform = `translate(${shiftX.toFixed(1)}px, 0)`;
			node.style.opacity = "1";
			img.style.transform = `translate(${band.srcDx.toFixed(1)}px, ${band.srcDy.toFixed(1)}px)`;
		};

		const clearFrame = (node: HTMLElement) => {
			node.style.opacity = "0";
			node.style.transform = "none";
			node.style.clipPath = "none";
			node.style.removeProperty("--glitch-color");
		};

		const clearAll = () => {
			chromaA.forEach(clearNode);
			chromaB.forEach(clearNode);
			sliceNodes.forEach(clearNode);
			clearFrame(frameA);
			clearFrame(frameB);
			root.classList.remove("is-glitching");
			burst = null;
		};

		const paintFrame = (node: HTMLElement, color: string, dx: number, dy: number) => {
			node.style.setProperty("--glitch-color", color);
			node.style.opacity = String(0.65 + 0.35 * intensity);
			node.style.transform = `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px)`;
			node.style.clipPath = "none";
		};

		const paint = () => {
			if (intensity <= 0.02 || !burst || size.w <= 0) {
				clearAll();
				return;
			}

			const chromaPx = 2 + 14 * intensity;
			// Chromatic pairs prefer the strongest (near-cursor) lines.
			const ranked = [...burst.slices].sort((a, b) => b.local - a.local);
			const pairCount = Math.min(MAX_CHROMA, ranked.length);
			chromaA.forEach((node, index) => {
				const band = index < pairCount ? ranked[index] : null;
				if (band) paintNode(node, band, chromaPx, -1, burst!.colorA);
				else clearNode(node);
			});
			chromaB.forEach((node, index) => {
				const band = index < pairCount ? ranked[index] : null;
				if (band) paintNode(node, band, chromaPx, 1, burst!.colorB);
				else clearNode(node);
			});

			sliceNodes.forEach((node, index) => {
				const band = index < burst!.slices.length ? burst!.slices[index] : null;
				if (band) paintNode(node, band, 0, 0);
				else clearNode(node);
			});

			const frameShift = 1 + 6 * intensity;
			paintFrame(frameA, burst.colorA, -frameShift, rand(-1.5, 1.5) * intensity);
			paintFrame(frameB, burst.colorB, frameShift, rand(-1.5, 1.5) * intensity);

			root.classList.add("is-glitching");
		};

		const reshuffle = () => {
			burst = buildBurst(cursor.x, cursor.y, size.w, size.h, intensity);
			paint();
		};

		const stop = () => {
			window.cancelAnimationFrame(raf);
			raf = 0;
			intensity = 0;
			clearAll();
		};

		const frame = (now: number) => {
			const dt = Math.min(0.05, (now - lastFrame) / 1000 || 0.016);
			lastFrame = now;

			if (now - lastMove > IDLE_MS) {
				intensity = Math.max(0, intensity - dt / (DECAY_MS / 1000));
			}

			if (now - lastTick >= TICK_MS) {
				lastTick = now;
				if (intensity > 0.02) reshuffle();
				else clearAll();
			} else if (intensity > 0.02) {
				paint();
			}

			if (intensity > 0.02 || (hovering && now - lastMove <= IDLE_MS)) {
				raf = window.requestAnimationFrame(frame);
				return;
			}

			if (intensity <= 0.02) {
				stop();
				return;
			}

			raf = window.requestAnimationFrame(frame);
		};

		const ensureLoop = () => {
			if (raf) return;
			lastFrame = performance.now();
			lastTick = lastFrame;
			raf = window.requestAnimationFrame(frame);
		};

		const syncSize = () => {
			size.w = root.clientWidth;
			size.h = root.clientHeight;
		};

		const onEnter = (event: PointerEvent) => {
			if (reduceMotion || !finePointer || event.pointerType !== "mouse") return;
			hovering = true;
			const rect = root.getBoundingClientRect();
			cursor.x = event.clientX - rect.left;
			cursor.y = event.clientY - rect.top;
			lastMove = performance.now();
			intensity = 1;
			reshuffle();
			ensureLoop();
		};

		const onMove = (event: PointerEvent) => {
			if (!hovering || event.pointerType !== "mouse") return;
			const rect = root.getBoundingClientRect();
			cursor.x = event.clientX - rect.left;
			cursor.y = event.clientY - rect.top;
			const now = performance.now();
			lastMove = now;
			intensity = 1;
			if (now - lastTick >= TICK_MS * 0.45) {
				lastTick = now;
				reshuffle();
			}
			ensureLoop();
		};

		const onLeave = () => {
			hovering = false;
			lastMove = performance.now() - IDLE_MS - 1;
			ensureLoop();
		};

		syncSize();
		const observer = new ResizeObserver(syncSize);
		observer.observe(root);

		if (!reduceMotion && finePointer) {
			root.addEventListener("pointerenter", onEnter);
			root.addEventListener("pointermove", onMove);
			root.addEventListener("pointerleave", onLeave);
		}

		return () => {
			observer.disconnect();
			root.removeEventListener("pointerenter", onEnter);
			root.removeEventListener("pointermove", onMove);
			root.removeEventListener("pointerleave", onLeave);
			stop();
		};
	}, []);

	const imgProps = {
		src,
		srcSet,
		sizes,
		width,
		height,
		decoding: "async" as const,
		draggable: false,
	};

	return (
		<figure ref={rootRef} className="home-hero-media">
			<img
				{...imgProps}
				alt={alt}
				loading="lazy"
				className="home-hero-media-base"
			/>
			{/* Static frame under the glitch so tears can break across it. */}
			<span className="home-hero-media-frame" aria-hidden="true" />
			<div className="home-hero-glitch" aria-hidden="true">
				{Array.from({ length: MAX_CHROMA }, (_, index) => (
					<div
						key={`ca-${index}`}
						className="home-hero-glitch-layer home-hero-glitch-chroma home-hero-glitch-chroma-a"
					>
						<img {...imgProps} alt="" />
					</div>
				))}
				{Array.from({ length: MAX_CHROMA }, (_, index) => (
					<div
						key={`cb-${index}`}
						className="home-hero-glitch-layer home-hero-glitch-chroma home-hero-glitch-chroma-b"
					>
						<img {...imgProps} alt="" />
					</div>
				))}
				{Array.from({ length: MAX_SLICE_NODES }, (_, index) => (
					<div
						key={`slice-${index}`}
						className="home-hero-glitch-layer home-hero-glitch-slice"
					>
						<img {...imgProps} alt="" />
					</div>
				))}
				{/* Duplicated frame strips that glitch with the same chromatic pairs. */}
				<span className="home-hero-glitch-frame home-hero-glitch-frame-a" />
				<span className="home-hero-glitch-frame home-hero-glitch-frame-b" />
			</div>
		</figure>
	);
}
